/**
 * Date utility functions for consistent date handling across the application
 */
/**
 * Checks if a date string is a .NET zero/default DateTime (0001-01-01T00:00:00).
 * The backend may send this value when a DateTime field has no value.
 * Use this to treat zero dates as empty/null instead of displaying "Jan 1, 0001".
 */
export const isZeroDate = (dateString) => {
    if (!dateString)
        return true;
    return dateString.startsWith('0001-01-01');
};
/**
 * Formats a date for backend submission, preserving the user's intended date and time
 * without unwanted timezone conversions
 */
export const formatDateForBackend = (dateString) => {
    if (!dateString)
        return '';
    try {
        // Parse the date string - this should work with ISO strings or date picker formats
        const date = new Date(dateString);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            throw new Error('Invalid date');
        }
        // Return ISO string format that the backend expects
        return date.toISOString();
    }
    catch (error) {
        console.error('Error formatting date for backend:', error);
        return dateString; // Return original if conversion fails
    }
};
/**
 * Formats a date string for display in the UI
 * Ensures consistent display regardless of how the date was stored
 */
export const formatDateForDisplay = (dateString) => {
    if (!dateString) {
        return { date: '', time: '' };
    }
    try {
        const date = new Date(dateString);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            throw new Error('Invalid date');
        }
        // Format date and time for display
        const formattedDate = date.toLocaleDateString();
        const formattedTime = date.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return {
            date: formattedDate,
            time: formattedTime
        };
    }
    catch (error) {
        console.error('Error formatting date for display:', error);
        return { date: dateString, time: '' };
    }
};
/**
 * Formats a date for the date picker input (editing scenarios)
 * Ensures the date picker shows the correct date/time when editing
 */
export const formatDateForInput = (dateString) => {
    if (!dateString)
        return '';
    try {
        const date = new Date(dateString);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            throw new Error('Invalid date');
        }
        // Return ISO string for the date picker
        return date.toISOString();
    }
    catch (error) {
        console.error('Error formatting date for input:', error);
        return dateString;
    }
};
/**
 * Creates a date object from user input, preserving their intended date/time
 */
export const createDateFromInput = (dateString) => {
    if (!dateString)
        return null;
    try {
        const date = new Date(dateString);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            throw new Error('Invalid date');
        }
        return date;
    }
    catch (error) {
        console.error('Error creating date from input:', error);
        return null;
    }
};
/**
 * Formats a date for date picker input (YYYY-MM-DD format)
 * Preserves the local date without timezone conversion issues
 */
export const formatDateForDatePicker = (date) => {
    if (!date)
        return '';
    try {
        const d = new Date(date);
        // Check if the date is valid
        if (isNaN(d.getTime())) {
            throw new Error('Invalid date');
        }
        // Format as YYYY-MM-DD using local timezone
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    catch (error) {
        console.error('Error formatting date for date picker:', error);
        return '';
    }
};
